import { useEffect, useRef, useState, type KeyboardEvent } from 'react'
import { PageHero } from '../components/PageHero'
import { TeamProfileCard } from '../components/TeamProfileCard'
import { TeamProfileDialog } from '../components/TeamProfileDialog'
import { images } from '../data/images'
import { teamMembers } from '../data/site'

export function Team() {
  const [activeIndex, setActiveIndex] = useState<number | null>(null)
  const openerRefs = useRef(new Map<number, HTMLButtonElement>())
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const dialogRef = useRef<HTMLElement>(null)
  const activeMember = activeIndex === null ? null : teamMembers[activeIndex]

  useEffect(() => {
    if (activeIndex === null) return
    document.documentElement.classList.add('profile-dialog-open')
    document.body.classList.add('profile-dialog-open')
    const frame = window.requestAnimationFrame(() => closeButtonRef.current?.focus())
    return () => {
      window.cancelAnimationFrame(frame)
      document.documentElement.classList.remove('profile-dialog-open')
      document.body.classList.remove('profile-dialog-open')
    }
  }, [activeIndex])

  const closeProfile = () => {
    const opener = activeIndex === null ? null : openerRefs.current.get(activeIndex)
    setActiveIndex(null)
    window.requestAnimationFrame(() => opener?.focus())
  }

  const handleDialogKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault()
      closeProfile()
      return
    }
    if (event.key !== 'Tab') return

    const focusable = Array.from(dialogRef.current?.querySelectorAll<HTMLElement>('button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])') ?? [])
    if (!focusable.length) return
    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  return (
    <>
      <PageHero eyebrow="Meet the Artists" title="Our Team" image={images['gallery-02']} objectPosition="48% center" />
      <section className="section shell team-directory-section">
        <div className="team-directory__intro reveal reveal--heading">
          <span className="eyebrow">The Professionals</span>
          <h2>Experience, artistry, and genuine care.</h2>
          <p>Choose a portrait to open a larger, easy-to-read profile and learn more about each professional.</p>
        </div>
        <ul className="team-directory">
          {teamMembers.map((member, index) => (
            <TeamProfileCard
              member={member}
              index={index}
              isExpanded={activeIndex === index}
              onOpen={() => setActiveIndex(index)}
              setButtonRef={(node) => {
                if (node) openerRefs.current.set(index, node)
                else openerRefs.current.delete(index)
              }}
              key={member.name}
            />
          ))}
        </ul>
      </section>
      {activeMember && activeIndex !== null && (
        <TeamProfileDialog
          member={activeMember}
          index={activeIndex}
          closeButtonRef={closeButtonRef}
          dialogRef={dialogRef}
          onClose={closeProfile}
          onKeyDown={handleDialogKeyDown}
        />
      )}
    </>
  )
}
